// Tipurile Pasului / zonei «pas7» — desprinse din `tipuri.ts`; orice importator
// le ia tot din `./tipuri` (bariera re-exportă fiecare fișier de aici).

import type { DecizieProiect } from "./retete";

// --- Pasul 7: modelul FEM ------------------------------------------------------

export interface FemNod {
  id: number;
  x_m: number;
  y_m: number;
  z_m: number;
  nivel: number;
  // „fundatie" = nod intermediar al rețelei de fundare (pasul resoartelor),
  // „sol" = nodul ÎNCASTRAT de sub resort (ambele la cota 0, v. fem_fundatie)
  tip: "baza" | "stalp" | "reazem" | "fem" | "master" | "placa" | "fundatie" | "sol";
}

// --- fundația în modelul de calcul (fem_fundatie.py) -------------------------

/** Un tronson de grindă de fundare: bară `elasticBeamColumn` ÎNTRE NODURILE
 *  DE BAZĂ, la cota 0. `B_m` e lățimea AXULUI lui (Pasul 17 lățește
 *  individual), nu lățimea globală. */
export interface FemGrindaFundare {
  nod_i: number;
  nod_j: number;
  L_m: number;
  ax: string;
  B_m: number;
  // cota de fundare și înălțimea secțiunii pe AXUL tronsonului: fiecare ax se
  // toarnă la adâncimea cerută de deschiderea lui,
  // iar desenul 3D coboară talpa la −Df_m, nu la o cotă comună
  Df_m?: number;
  H_c_m?: number;
  element_id?: number;
}

/** Resortul de teren: `zeroLength` între nodul de sol (încastrat) și nodul
 *  structural, cu k_v pe direcția 3 și k_h pe 1 și 2. Rotirile rămân LIBERE. */
export interface FemResort {
  id: number;
  nod: number;
  nod_sol: number;
  kv_kn_m: number;
  kh_kn_m: number;
  l_afer_m: number;
  arie_afer_m2?: number;
  B_mediu_m?: number;
}

/** Geometria și rigiditatea fundației din model — secțiunea de EXECUȚIE
 *  (sectiune_fundatie.py), din care se desenează betonul. */
export interface FemFundatie {
  detaliu?: "T" | "bloc" | "pamant";
  detaliu_descriere?: string;
  /** RADIERUL (`fem_fundatie.adauga_radier`): grosimea, amprenta și conturul
   *  desenat — componentele amprentei, fiecare cu exteriorul și golurile ei,
   *  în cadrul nodurilor. Scena îl extrudează cu h_r sub pardoseala de jos. */
  radier?: { h_m: number; amprenta_m2?: number; contur?: [number, number][][][] } | null;
  B_m?: number;
  Df_m?: number | null;
  H_c_m?: number;
  h_talpa_m?: number;
  b_inima_m?: number;
  h_peste_m?: number;
  h_bloc_m?: number;
  ks_dan_cm3?: number;
  ks_kn_m3?: number;
  ks_sursa?: string;
  raport_orizontal?: number;
  fara_intindere?: boolean;
  pas_resoarte_m?: number;
  // greutatea proprie pe metru liniar (cazul G4) și lestul care se opune
  // ridicării — pe secțiunea GLOBALĂ; axele lățite au valorile lor în `pe_ax`
  q_kn_ml?: number;
  g_lest_kn_ml?: number;
  lungime_elastica_m?: number | null;
  masa_infrastructura_t?: number;
  masa_infrastructura_pct?: number;
  masa_infrastructura_seismica?: boolean;
  sectiune?: { A_m2?: number; Iy_m4?: number; Iz_m4?: number; J_m4?: number };
  pe_ax?: Record<string, {
    B_m?: number;
    // cota și înălțimea LUI (v. FemGrindaFundare)
    Df_m?: number;
    H_c_m?: number;
    L0_m?: number;
    h_talpa_m?: number;
    b_inima_m?: number;
    h_inima_m?: number;
    h_peste_m?: number;
    descriere?: string;
    A_m2?: number;
    Iy_m4?: number;
    q_kn_ml?: number;
    g_lest_kn_ml?: number;
    v_beton_m3_ml?: number;
    v_umplutura_m3_ml?: number;
  }>;
}

/** Un radier de desenat: al corpului singur (din `fundatie.radier`) sau al
 *  unui corp al complexului (`model.radiere`), de la pardoseala lui de jos
 *  (`z_sus_m`) până la −h_r, pe balastul plăcii pe sol. */
export interface FemRadier {
  /** vederea complexului: al cărui corp e radierul */
  corp?: string;
  h_m: number;
  balast_m?: number | null;
  amprenta_m2?: number | null;
  z_sus_m: number;
  z_jos_m: number;
  contur: [number, number][][][];
}

export interface FemSectiuneT {
  A_m2: number;
  yc_sus_m: number;
  I_puternic_m4: number;
  I_slab_m4: number;
  J_m4: number;
}

export interface FemGrinda {
  etaj: number;
  nivel: string;
  // id_plan-ul global (GR7) — referința elementului în toate tabelele
  id_plan?: string | null;
  /** vederea complexului: al cărui corp e grinda */
  corp?: string;
  nod_i: number;
  nod_j: number;
  noduri: number[];
  L_m: number;
  bw_m: number;
  h_m: number;
  hf_m: number;
  element_ids: number[];
  // capetele AXEI REALE (nod + zona rigidă din stâlp), pentru desenul 3D —
  // fără ele grinda se desena până în nodul stâlpului (oblică la L/T-uri)
  ofs_a_xy?: [number, number];
  ofs_b_xy?: [number, number];
  // varianta lean (grinda T cu b_eff)
  b_eff_m?: number;
  b_eff_laturi_m?: number[];
  l0_m?: number;
  tip_deschidere?: string;
  latime_tributara_m?: number;
  sectiune_T?: FemSectiuneT;
  sectiune_bruta?: { A_m2: number; I_puternic_m4: number; I_slab_m4: number; J_m4: number };
  q_dead_kn_m?: number;
  q_sdl_kn_m?: number;
  q_live_kn_m?: number;
  // varianta shell (doar inima sub placă + offset compozit)
  h_web_m?: number;
  e_offset_m?: number;
  sectiune_web?: { A_m2: number; I_puternic_m4: number; I_slab_m4: number; J_m4: number };
  q_web_kn_m?: number;
}

export interface FemShell {
  id: number;
  nivel: number;
  noduri: number[]; // 4 noduri
  arie_m2: number;
  // laturile celulei, pe axele DOMINANTE ale clădirii (cadrul rotit al
  // mesh-ului), nu pe axele globale
  dx_m?: number;
  dy_m?: number;
  // panoul («ochiul») de placă din care face parte celula — index în
  // FemModel.panouri; null dacă celula nu cade în niciun ochi închis de
  // rețeaua de grinzi (placă venită din amprenta nivelului)
  panou?: number | null;
  sectiune_tag?: number;
  cat?: string;     // categoria de funcțiune (A / C) — decide utila
  // celula n-are altă placă deasupra: acoperiș sau terasă rămasă din
  // retragerea unui etaj. Pe ea cade zăpada (cazul S). Cheia se scrie numai
  // când e adevărată — pe o clădire cu 20.000 de celule, un `false` peste tot
  // ar fi greutate moartă în model.
  expus?: boolean;
}

// un OCHI de placă: panoul dintre reazemele lui, obținut tăind amprenta
// nivelului cu axele grinzilor și ale pereților. `contur` e în cadrul GLOBAL.
export interface FemPanou {
  id: string;              // OP3.1 = ochiul 3 de la nivelul 1
  nivel: number;
  nivel_nume?: string;
  // gabaritul pe axele dominante; `deschidere_m` e diametrul celui mai mare
  // cerc înscris — deschiderea mică reală, definită și pentru un panou în L
  lx_m: number;
  ly_m: number;
  deschidere_m?: number;
  arie_m2: number;
  // aria și numărul de elemente finite atribuite efectiv panoului
  arie_mesh_m2?: number;
  nr_shells?: number;
  // cum se închide conturul (% din perimetru): grinzi, pereți de arhitectură,
  // marginea clădirii. Însumează 100.
  pe_grinzi_pct?: number;
  pe_pereti_pct?: number;
  pe_contur_pct?: number;
  cat?: string;            // A / C — categoria majorității ariei
  arie_c_m2?: number;
  contur: [number, number][];
  perimetru_m?: number;
  // conturul spart în laturi OMOGENE: fiecare cu reazemul ei și cu vecinul de
  // peste reazem. Pe ele se desenează schema statică a ochiului în Pasul 16.
  laturi?: FemLaturaOchi[];
  // golurile interioare, când ochiul e un INEL (v. `fem_shell._fara_crestaturi`)
  inele?: {
    arie_m2: number;
    perimetru_m: number;
    contur: [number, number][];
    laturi?: FemLaturaOchi[];
  }[];
  // consolele (balcoanele) care ies din ochi și pereții care stau PE el
  console?: {
    tip: string;
    adanc_m: number;
    arie_m2?: number | null;
    L_prindere_m?: number | null;
    prindere?: string;
    dist_ochi_m?: number;
  }[];
  pereti_pe_placa?: {
    a: [number, number];
    b: [number, number];
    L_m: number;
    q_kn_ml: number;
    sursa: string;
    // peretele stă pe o CONSOLĂ: încarcă capătul balconului, deci intră în
    // momentul de încastrare al Pasului 16, nu doar în câmpul ochiului
    pe_balcon?: boolean;
  }[];
  zone_gol?: { eticheta: string; arie_pe_ochi_m2: number;
               contur?: [number, number][] }[];
}

// o latură de ochi: segmentul de contur cu UN singur fel de reazem
export interface FemLaturaOchi {
  a: [number, number];
  b: [number, number];
  L_m: number;
  reazem: "grinda" | "perete" | "contur_fara_reazem";
  t_reazem_m?: number | null;
  continua?: boolean;
  vecin?: string | null;
  zimtata?: boolean;
}

export interface FemStalp {
  etaj: number;
  nivel: string;
  nod_jos: number;
  nod_sus: number;
  /** cum se NUMEȘTE elementul: «stâlp» sau «lamelă» (peste raportul de
   *  zveltețe e normativ perete și se și dimensionează ca perete). Id-ul
   *  rămâne al stâlpilor — e același element, din aceeași familie. */
  tip_element?: string;
  /** amprenta reală (m, absolută) a stâlpului în L/T; lipsește la dreptunghiuri,
   *  unde cutia h_m × b_m pe `dir` E amprenta */
  amprenta_m?: [number, number][];
  /** brațele stâlpului în T, L sau +: piciorul de pe axa cealaltă (b × h,
   *  la `la_m` de-a lungul piciorului principal), turnat cu el; lipsește la
   *  dreptunghiuri */
  brate?: { b_m: number; h_m: number; la_m?: number }[];
  /** aria și inerțiile COMPUSE ale poligonului, la stâlpul cu brațe;
   *  lipsesc la dreptunghi, unde b × h le spune */
  A_m2?: number;
  I_dir_m4?: number;
  I_perp_m4?: number;
  // b_m = latura TRANSVERSALĂ, h_m = latura de-a lungul lui `dir` (convenția
  // din model.py: dim = {L_mm de-a lungul versorului u, b_mm transversal})
  b_m: number;
  h_m: number;
  // versorul pe care stă latura lungă — direcția peretelui stâlpului. Fără el
  // desenul 3D punea latura lungă pe axa globală X, deci pe o clădire rotită
  // (aici 16°) fiecare stâlp alungit apărea strâmb și ieșit din zid.
  dir?: [number, number] | null;
  id_plan?: string;
  element_id: number;
  /** vederea complexului: al cărui corp e piesa (id-ul e deplasat cu 1000·k) */
  corp?: string;
}

/** `norme.RAPORT_LAMELA`: latura lungă de cel puțin 4 ori cea scurtă. */
const RAPORT_LAMELA = 4;

/** Nivelul ăsta al stivei e LAMELĂ — se armează la Pasul 14, cu pereții —,
 *  nu stâlp (Pasul 15)? Aceeași regulă ca rutarea serverului
 *  (`armare_stalpi.e_lamela`): raportul laturilor h × b ale inimii, brațele
 *  unui L/T nu contează, iar o latură lipsă e 30 cm.
 *
 *  NU din `tip_element`: modelele blocurilor generate nu-l poartă deloc (pe
 *  Demo, C4-T2, niciunul din cei 336 de stâlpi, toți lamele), iar drawer-ul
 *  cerea pentru lamela aleasă Pasul 15, n-o găsea acolo și arăta «devizul
 *  lipsește» în loc de cost. Iar unde îl poartă, vine din cotele Pasului 4,
 *  nu din secțiunea modelului după care rutează serverul. */
export function eLamela(s: Pick<FemStalp, "h_m" | "b_m">): boolean {
  const a = Math.abs(s.h_m || 0.3);
  const b = Math.abs(s.b_m || 0.3);
  const lung = Math.max(a, b);
  const scurt = Math.min(a, b);
  return scurt > 0 && lung / scurt >= RAPORT_LAMELA;
}

export interface FemElement {
  id: number;
  tip: "stalp" | "grinda" | "grinda_fundare";
  ref: number;
  nod_i: number;
  nod_j: number;
  A_m2: number;
  Iy_m4: number;
  Iz_m4: number;
  J_m4: number;
  vecxz: number[];
  offset_i: number[];
  offset_j: number[];
  // factorul de fisurare DEJA aplicat pe I/J de mai sus, cu preferința din
  // care vine — inspectorul Pasului 7 îl arată ca să nu pară că I e brut
  fisurare?: number;
  sursa_fisurare?: string;
  // EXACT argumentele cu care se cheamă ops.element(...) / ops.geomTransf(...)
  // pentru elementul ăsta (fem_shell.descrie_opensees) — ce se vede în
  // interfață e ce rulează în solver
  opensees?: OpenSeesComanda;
}

// descrierea OpenSees a unui element: lista de argumente, exact în ordinea
// apelului. Primul element e numele comenzii.
export type OpenSeesArg = number | string;

export interface OpenSeesComanda {
  element: OpenSeesArg[];
  transf?: OpenSeesArg[];
  materiale?: OpenSeesArg[][];
}

// o secțiune de shell (ElasticMembranePlateSection): placa și câte una per
// grosime de perete de subsol
export interface FemSectiuneShell {
  tag: number;
  rol: "placa" | "perete_subsol";
  t_m: number;
  nu: number;
  rho: number;
  fisurare: number;
  sursa_fisurare: string;
  E_kpa: number;
  E_brut_kpa: number;
  sursa_grosime: string;
  opensees?: OpenSeesArg[];
}

// o fâșie de shell dintr-un panou de perete de subsol
export interface FemShellPerete {
  id: number;
  noduri: number[];
  perete: number;           // index în FemModel.pereti_beton
  id_plan: string;
  nivel: number;
  t_m: number;
  x0_m: number;
  x1_m: number;
  latime_m: number;
  h_m: number;
  arie_m2: number;
  greutate_kn: number;
  sectiune_tag?: number;
}

export interface FemDiafragma {
  etaj: number;
  nivel: string;
  nivel_diafragma: number;
  z_m: number;
  cm: [number, number];
  masa_t: number;
  jz_t_m2: number;
  greutate_dead_kn: number;
  greutate_sdl_kn: number;
  greutate_live_kn: number;
  greutate_seismica_kn: number;
  master: number;
}

export interface FemEtaj {
  index: number;
  nivel: string;
  nivel_diafragma: number;
  z_m: number;
  inaltime_m?: number; // înălțimea etajului (Pasul 2 → Pasul 4)
  arie_m2: number;
  slab: [number, number][][][]; // [componenta][inel][punct]
  // pereții nestructurali ai nivelului (layerul gri din 3D), fiecare cu
  // id-ul lui din Pasul 2; absent la rezultatele rulate înainte de el
  pereti?: FemPereteZidarie[];
  // …și cei care NU mai sunt acolo, fiindcă s-a turnat beton peste ei
  zidarie_turnata?: FemZidarieTurnata[];
  // ferestrele și ușile nivelului (layere proprii în 3D, stinse implicit);
  // absente la rezultatele rulate înainte de introducerea lor
  goluri?: FemGol[];
  // parapetele / balustradele nivelului — „marginile" nestructurale ale
  // Pasului 2. Aceeași formă ca pereții de zidărie, plus înălțimea cu care
  // intră în încărcări și mase (presupusă 0,90 m când planul n-o cotează)
  margini?: FemPereteZidarie[];
  // casele de scară și lifturile nivelului (zonele `scara`/`lift` ale
  // Pasului 2): doar afișare — nucleul e locul în care structuristul pune
  // pereții mari, deci trebuie să se vadă unde e. Absente la rezultatele
  // rulate înainte de introducerea lor
  nuclee?: FemNucleu[];
  // balcoanele plăcii acestui nivel (plăcile în consolă din Pasul 2):
  // sub 2 m ieșire = placă de beton simplă, fără grinzi și fără armare
  balcoane?: FemBalcon[];
  ochiuri: number;
  // de unde vine conturul de planșeu (varianta shell): ochiurile închise de
  // rețeaua de grinzi, amprenta construită a nivelului (Pasul 2) și
  // suprafața dintre stâlpi. Diferențele dintre ele spun dacă placa acoperă
  // tot nivelul sau doar cât a închis rețeaua.
  arie_ochiuri_m2?: number;
  arie_amprenta_m2?: number;
  arie_hull_stalpi_m2?: number;
  nr_ochiuri?: number;
  grinzi_in_afara?: string[];
  nr_shells?: number;
}

// casa scării sau puțul liftului unui nivel, așa cum le-a citit importul:
// `dedus` = recunoscut după formă (camera de deasupra scării etichetate pe alt
// nivel, puțul lipit de scară), nu după ștampila arhitectului
export interface FemNucleu {
  id: string;
  kind: "scara" | "lift";
  eticheta?: string | null;
  dedus?: boolean;
  inele: [number, number][][];  // [inel][punct]: exteriorul, apoi golurile
  // scara citită din liniile planșei (`arhimport.scara.citeste`): rampele
  // ca linii de urcare, în metri, cu contratreptele fiecăreia — 3D-ul
  // desenează treptele exact cum sunt pe planșă; absentă, desenează scara
  // tipului în cutia casei scării (`scari3d.scaraImplicita`)
  scara?: {
    fel?: string | null;
    contratrepte?: number | null;
    lat_rampa_m?: number;
    treapta_m?: number;
    rampe: { de: [number, number]; la: [number, number]; contratrepte: number }[];
  };
}

// peretele NESTRUCTURAL al unui nivel: peretele simplificat al Pasului 2, cu
// porțiunile turnate din beton deja scoase (un perete tăiat în două dă două
// intrări cu același id_plan)
export interface FemPereteZidarie {
  id_plan: string;          // S7, E14, … — id-ul din Pasul 2
  /** vederea complexului: al cărui corp e zidul */
  corp?: string;
  t_m: number;
  L_m: number;
  inele: [number, number][][];  // [inel][punct]: exteriorul, apoi golurile
  // materialul și rolul DECLARATE de arhitectură (`beton` / `zidarie` /
  // `polistiren`). Lipsesc pe proiectele importate din DWG — planșa nu le
  // scrie nicăieri —, iar atunci panoul spune «zidărie, presupus» în loc să
  // inventeze o declarație.
  material?: string;
  rol?: "structural" | "nestructural";
  // doar la parapete (margini): înălțimea cu care intră în model — cotată în
  // dosar, sau presupusă (0,90 m) când planul n-o dă
  h_m?: number;
  inaltime_presupusa?: boolean;
  // piesele zidului din jurul golurilor lui cotate (`fem.pereti_cu_goluri`):
  // șpaleții pe toată înălțimea, parapetul de sub fereastră, buiandrugul de
  // peste gol. Când există, 3D-ul le desenează pe ele în locul cutiei pline
  piese?: FemPiesaZid[];
}

export interface FemPiesaZid {
  fel: "spalet" | "parapet" | "buiandrug";
  z0_m: number;                 // de la pardoseala nivelului
  z1_m: number;
  inele: [number, number][][];  // ca la perete: exteriorul, apoi găurile
}

// peretele Pasului 2 peste care s-a TURNAT beton: piesele care i-au luat
// locul și cât la sută din el au luat. Contrapartida lui `FemEtaj.pereti` —
// un perete înghițit de o lamelă nu mai e în layerul nestructural, iar fără
// lista asta ar dispărea din 3D fără explicație.
export interface FemZidarieTurnata {
  id_plan: string;
  pct: number;              // cât la sută din perete s-a turnat, în total
  t_m?: number;
  L_m?: number;
  // piesele de beton care i-au luat locul, fiecare cu partea EI din perete,
  // în ordine descrescătoare: pe E64, lamela ST117.6 ia 99 %, iar stâlpii
  // vecini ciupesc 6 % fiecare
  elemente: { id: string; pct: number }[];
}

// GOLUL unui nivel: fereastra sau ușa citită de import (Pasul 2), desenată în
// 3D ca layer propriu. Nu intră în calcul — e arhitectura, nu structura.
//
// `hp_m` (parapetul) e CITIT de pe planșă, din marcajul „HP=0.90m", și e
// măsurat de la PARDOSEALA FINITĂ a nivelului. `h_m` (înălțimea golului) e
// MĂSURAT pe planșa de fațade, unde golurile sunt desenate la scară — planul
// nu cotează buiandrugul. Amândouă lipsesc când dosarul nu le dă; atunci
// rămân `null` și 3D-ul o SPUNE, nu completează.
export interface FemGol {
  id_plan: string;          // F3 (fereastră) / U7 (ușă) — id-ul din Pasul 2
  tip: "usa" | "fereastra";
  L_m: number;
  t_m: number;
  hp_m: number | null;      // parapetul, de la pardoseala finită
  h_m: number | null;       // înălțimea golului, măsurată pe fațadă
  perete: string | null;    // peretele-gazdă, dacă golul stă într-unul
  colturi: [number, number][];
}

export interface FemBalcon {
  contur: [number, number][];
  arie_m2?: number | null;
  adanc_m?: number;   // cât iese placa în afara amprentei construite
  // logie = nu iese din amprentă (planșeul trece pe acolo, nu e consolă);
  // balcon = consolă sub 2 m, placă de beton simplă; balcon_structura =
  // consolă peste 2 m, cere grinzi/stâlpi; terasa_pe_placa = terasa unui
  // etaj retras, peste nivelul de dedesubt: o parte din ochiul de placă de
  // acolo, rezemată pe grinzile lui, nu o consolă
  tip?: "logie" | "balcon" | "balcon_structura" | "terasa_pe_placa";
}

// perete de beton armat modelat ca grindă: panoul dintre stâlpii subsolului
// (cutia rigidă, PB…) sau peretele de pe latura unui balcon exterior pe care
// ARHITECTURA îl cere din beton (PBB… — preferința `pereti_balcon_beton`;
// implicit laturile de balcon sunt zidărie și nu ajung aici)
export interface FemStalpisorZc {
  perete: string;
  tip: "capat" | "intersectie" | "intermediar";
  xy: [number, number];
  latura_m: number;
  t_m: number;
  z_jos_m: number;
  z_sus_m: number;
  u: [number, number];      // versorul peretelui, cadrul global
}

export interface FemPereteBeton {
  etaj: number;
  nivel: string;
  id_plan: string;          // PB1, PB2, … / PBB1, PBB2, …
  tip?: "subsol" | "balcon" | "zidarie_confinata";
  /** vederea complexului: al cărui corp e panoul; «parcare» = peretele
   *  exterior al parcării, desenat pe conturul subsolului, necalculat */
  corp?: string;
  parcare?: boolean;
  // laturile de balcon: id-ul peretelui de ARHITECTURĂ din care s-a turnat
  // (același pe care omul îl scrie în preferința `pereti_balcon_beton`)
  id_arh?: string | null;
  nod_i: number;
  nod_j: number;
  stalp_i?: string | null;
  stalp_j?: string | null;
  t_m: number;
  h_web_m: number;
  hf_m: number;
  L_m: number;
  off_i_m: number;
  off_j_m: number;
  a_xy: [number, number];   // colț de stâlp → colț de stâlp (cadrul global):
  b_xy: [number, number];   // exact panoul turnat, între fețele stâlpilor
  n_exterior: [number, number]; // normala spre fața exterioară
  e_fata_m: number;         // cât e împins față de AXA stâlpilor (informativ)
  z_sus_m: number;
  perimetral?: boolean;
  q_kn_m: number;
  // gol: panoul NU e o bară, ci un rând de shell-uri
  element_ids?: number[];
  nod_jos_i?: number;
  nod_jos_j?: number;
  shell_ids?: number[];
  nr_shells?: number;
  statii_x_m?: number[];    // diviziunile pe lungime (tăieturile de secțiune)
  h_shell_m?: number;       // înălțimea GEOMETRICĂ a fâșiilor (etajul întreg)
  z_centroid_m?: number;    // centroidul secțiunii de calcul (t × h_web)
  e_offset_m?: number;      // coborârea rigidă a axei sub nodul plăcii
  h_ocupat_m?: number;      // ce stă peste panou: placa (t) sau grinda (h)
  grinda_deasupra?: boolean;
  // CE ȚINE PANOUL, scris de model (fem_shell.asaza_pereti_pe_infrastructura),
  // nu dedus de desen: un perete de beton se toarnă pe fundație, nu plutește
  // peste ea. `h_web_m` rămâne înălțimea de CALCUL (greutate, masă, armare);
  // `h_desen_m` e cea turnată, între cele două cote de mai jos.
  z_jos_m?: number;         // fața de jos: −h_peste al axului / fața plăcii
  z_sus_desen_m?: number;   // fața de jos a plăcii sau a grinzii de deasupra
  h_desen_m?: number;
  rezemare?: "fundatie" | "placa_pe_sol" | "perete" | "placa_nivel" | "cota_zero";
  ax_fundatie?: string | null;
}

// verdictul subsolului rigid: k_subsol ≥ 1,5 × max(k nivelurilor de deasupra)
export interface SubsolRigid {
  cerut: boolean;           // P+2 sau peste, cu subsol
  activ: boolean;           // există panouri de perete în model
  motiv?: string;
  nr_pereti: number;
  grosime_m?: number | null;
  prag: number;
  niveluri: {
    nivel: string;
    subteran: boolean;
    h_m: number;
    k_x_kn_m: number;
    k_y_kn_m: number;
  }[];
  raport_x?: number;
  raport_y?: number;
  trece?: boolean;
}

export interface FemMod {
  mod: number;
  T_s: number;
  f_hz: number;
  masa_x_pct: number;
  masa_y_pct: number;
  masa_rz_pct: number;
  masa_z_pct?: number;   // doar varianta shell (moduri verticale/locale de placă)
  // distribuția participării ACESTUI mod între X/Y/Z/torsiune — sumă 100%
  // (coloanele masa_* sunt raportate la baze diferite și NU sumează pe rând)
  dist_x_pct?: number;
  dist_y_pct?: number;
  dist_z_pct?: number;
  dist_rz_pct?: number;
  tip?: string;          // doar varianta shell (X / Y / torsiune / vertical / mixt)
  // cât de «pur» e modul: fracțiunea din participarea totală de pe direcția
  // dominantă (1,0 = translație pură; ~0,6 = translație cuplată cu torsiune)
  puritate?: number;
  // pereche cvasi-degenerată (perioade practic egale): `puritate` devine a
  // PERECHII, `puritate_mod` rămâne cea a modului luat singur — care nu
  // înseamnă nimic, solverul alege arbitrar o combinație a celor două
  pereche_cu?: number;
  puritate_mod?: number;
  nota_pereche?: string;
  cumulat_x_pct: number;
  cumulat_y_pct: number;
  // factorii de participare / masa efectivă pe direcțiile rotite (Pasul 9)
  gamma_theta?: Record<string, number>;
  meff_theta_t?: Record<string, number>;
  // deplasările normalizate [ux, uy, uz] per nod (ordinea model.noduri)
  forma?: number[][];
}

// gate-ul de moduri DECUPLATE (regula structuristului):
// T1/T2/T3 separate cu ≥ prag_separare_t_pct, modurile 1–2 translații cu
// ≥ prag_directie_pct pe câte o singură direcție, pe direcții diferite
export interface FemDecuplare {
  ok: boolean;
  prag_separare_t_pct?: number;
  prag_directie_pct?: number;
  moduri?: {
    mod: number;
    T_s: number;
    directie: string;
    directie_pct: number;
    masa_rz_pct?: number;
    delta_T_pct?: number;
    tip?: string;
    pereche_cu?: number;
  }[];
  motive?: string[];
  directie_recomandata?: string;
  nota?: string;
  // cele PATRU verificări numite, cu verdict individual:
  // separarea T1–T2, separarea T2–T3, direcția modului 1, direcția modului 2
  verificari?: {
    id: string;
    nume: string;
    valoare_pct?: number;
    prag_pct: number;
    ok: boolean | null;
    /** separare între `prag_review_pct` și prag: raportată «de revizuit» */
    review?: boolean;
    prag_review_pct?: number;
    formula?: string;
    directie?: string;
    nota?: string;
  }[];
}

export interface FemVerificare {
  metoda?: string;
  nr_moduri?: number;              // moduri REȚINUTE (criteriul de 90%)
  nr_moduri_extrase?: number;      // câte a scos eigen
  nr_moduri_min_cerut?: number;    // minimul din Preferințe
  mod_fundamental?: number;
  nota_moduri?: string;
  moduri?: FemMod[];
  /** formele modale lipsesc DINADINS din rezultat: se cer separat, prin
   *  `getFormeModale` (`steps/model_fem/forme`), numai când se desenează */
  forme_separat?: boolean;
  masa_cumulata_x_pct?: number;
  masa_cumulata_y_pct?: number;
  meff_cumulat_theta_pct?: Record<string, number>;
  meff_la_nr_moduri_min?: Record<string, number>;
  masa_totala_t?: number;
  criteriu_90_ok?: boolean;
  decuplare?: FemDecuplare;
  // ‖K·f − I‖ pe modelul condensat — doar varianta lean, pur informativ
  asimetrie_flexibilitate?: string;
  avertisment?: string;
  eroare?: string;
}

export interface FemModel {
  format: string;
  parametri: Record<string, unknown>;
  material: {
    beton: string;
    E_kpa: number;
    G_kpa: number;
    gamma_kn_m3: number;
    // E-ul PLĂCII: E_kpa scalat cu rigiditate_analiza_placa (varianta shell)
    E_placa_kpa?: number;
  };
  inaltime_etaj_m: number;      // media (informativ)
  inaltimi_etaj_m?: number[];   // înălțimile reale per etaj (Pasul 2 → Pasul 4)
  z_niveluri_m?: number[];      // cotele cumulate ale nivelurilor
  grosime_placa_m?: number;
  // unghiul grilei dominante (Pasul 2, prin fem_shell): conturul panourilor e
  // în axele globale, iar deschiderile x/y ale ochiurilor în axele grilei
  theta_rad?: number;
  noduri: FemNod[];
  noduri_baza: number[];
  stalpi: FemStalp[];
  grinzi: FemGrinda[];
  // pereții de beton ai subsolului (cutia rigidă) + verdictul de rigiditate
  pereti_beton?: FemPereteBeton[];
  // pereții STRUCTURALI din zidărie confinată ai etajului retras-proeminență
  // (`zidarie_confinata`): același format ca panourile de subsol, alt material
  pereti_zidarie?: FemPereteBeton[];
  // stâlpișorii lor de beton (fără elementele venite de jos, care sunt stâlpi)
  stalpisori_zc?: FemStalpisorZc[];
  subsol_rigid?: SubsolRigid;
  elemente: FemElement[];
  diafragme?: FemDiafragma[];  // doar varianta lean
  shells?: FemShell[];         // doar varianta shell
  shells_pereti?: FemShellPerete[];   // fâșiile panourilor de subsol
  sectiuni_shell?: FemSectiuneShell[];
  panouri?: FemPanou[];        // ochiurile de placă (varianta shell)
  mase?: [number, number][];   // [nod, masă t] — ce primește ops.mass
  masa_infrastructura?: [number, number][];  // greutate, dar NU masă seismică
  // fundația în modelul de calcul: secțiunea de execuție + rețeaua de grinzi
  // pe resoarte Winkler (lipsesc când baza e încastrată)
  fundatie?: FemFundatie;
  grinzi_fundare?: FemGrindaFundare[];
  resoarte?: FemResort[];
  noduri_sol?: number[];
  // …iar când baza E încastrată, betonul de sub clădire există fizic, doar
  // că nu participă la calcul: geometria lui vine separat, ca să se poată
  // DESENA (layer stins implicit). Nu are resoarte, noduri de sol sau
  // elemente — v. `fem_fundatie.geometrie_de_desen`.
  grinzi_fundare_desen?: FemGrindaFundare[];
  fundatie_desen?: FemFundatie;
  // placa pe sol: element REAL, dar nestructural — nu e în modelul FEM (se
  // reazemă direct pe teren). Se desenează, ca să nu pară că lipsește ceva
  // între stâlpi și fundație; aria și grosimea vin de unde le ia devizul.
  /** vederea complexului: radierul fiecărui corp, la cota lui — placa pe sol
   *  a unui corp pe radier nu se mai desenează, radierul e pardoseala lui */
  radiere?: FemRadier[];
  placa_pe_sol?: {
    contur: [number, number][][][];
    /** fiecare componentă a amprentei e un element cu id propriu (PS1, PS2…);
     *  pe complex, placa parcării are `corp = "parcare"` (PSP1…) */
    elemente?: { element: string; arie_m2: number; corp?: string; parcare?: boolean }[];
    arie_m2?: number;
    grosime_m: number;
    balast_m?: number;
    clasa?: string;
    sursa_grosime?: string;
    z_sus_m: number;
    z_jos_m?: number;
    nivel?: string;
    nota?: string;
  } | null;
  pereti_fundatie?: Record<string, unknown>;
  // terenul și săpătura: nu sunt în calcul, dar există pe șantier și se
  // plătesc. Geometria e a serverului (`sapatura.py`) — aceeași din care ies
  // volumele devizului, ca desenul și cifra să nu poată spune două lucruri.
  sapatura?: FemSapatura | null;
  etaje: FemEtaj[];
  avertismente: string[];
  // PILELE: stâlpi care nu coboară la fundație, ci se reazemă pe o grindă a
  // nivelului de dedesubt. Reazemul e ARTICULAT (M = 0 la bază): pila e o
  // pârghie prinsă în grinda de deasupra, iar în gazdă intră numai N; V-ul ei
  // se duce în planșeu. `stalp`/`gazda` sunt id-uri de plan cu sufixul de
  // nivel („ST90.6" pe „GR47.5"). Lipsesc pe modelele fără pile.
  articulatii?: FemArticulatie[];
  stalpi_plantati?: FemStalpPlantat[];
  // grinda-gazdă → stâlpii care se reazemă pe ea, cu abscisa pe grindă
  grinzi_transfer?: Record<string, FemStalpPeGrinda[]>;
}

/** O articulație de bază: pila `stalp` stă pe grinda `gazda`, în nodul `nod`
 *  al pilei, legat de nodul `pe_nod` al grinzii cu rotirea liberă. */
export interface FemArticulatie {
  stalp: string;
  gazda: string;
  nod: number;
  pe_nod: number;
}

/** Un stâlp plantat pe o grindă a nivelului de dedesubt: `x_m` e abscisa pe
 *  grindă, de la capătul i. `rezemat_pe_grinda` = grinda îl poartă (nu e doar
 *  o coincidență de plan); `articulat` = baza lui e articulată (pilă). */
export interface FemStalpPlantat {
  stalp: string;
  pe_grinda: string;
  x_m: number;
  rezemat_pe_grinda?: boolean;
  articulat?: boolean;
}

export interface FemStalpPeGrinda {
  stalp: string;
  x_m: number;
  rezemat_pe_grinda?: boolean;
}

/** Un strat din studiul geotehnic, cu cotele lui pe axa z a modelului. */
export interface FemStratTeren {
  nume?: string | null;
  de_la_m: number;
  pana_la_m: number;
  z_sus_m: number;
  z_jos_m: number;
  tip?: string | null;
  plasticitate?: string | null;
  gamma_kn_m3?: number | null;
  // clasa de terasament (v. `sapatura.CLASE_TEREN`) — din ea ies culoarea și
  // densitatea punctelor cu care se desenează stratul
  clasa: string;
  eticheta_clasa: string;
}

export interface FemRegimSapatura {
  clasa: string;
  eticheta: string;
  panta_m: number;
  panta_scrisa: string;
  h_vertical_m: number;
  vertical: boolean;
  /** ce mal descrie: «șanțului» sau «gropii» */
  ce?: string;
  adancime_m: number;
  /** cota crestei malului, sub CTN */
  de_la_m?: number;
  strat_determinant?: string | null;
  sursa: string;
  articol: string;
  apa_subterana_m?: number | null;
  note: string[];
  explicatie: string;
}

export interface FemSapatura {
  // DOUĂ maluri, deci două regimuri: al șanțului pornește din talpa gropii,
  // unde straturile de la suprafață nu mai există
  regim: FemRegimSapatura;
  regim_groapa?: FemRegimSapatura | null;
  teren?: {
    z_ctn_m: number;
    straturi: FemStratTeren[];
    adancime_studiu_m: number;
    apa_subterana_m?: number | null;
    z_apa_m?: number | null;
    sursa?: string | null;
    nota: string;
  } | null;
  // groapa cutiei de subsol: inelele de jos și de sus au ACELAȘI număr de
  // vârfuri, ca peretele înclinat să se poată lega vârf cu vârf
  groapa?: {
    arie_m2: number;
    perimetru_m: number;
    H_m: number;
    z_sus_m: number;
    z_jos_m: number;
    volum_m3: number;
    volum_vertical_m3: number;
    panta_m: number;
    /** cât s-a lărgit amprenta pentru cofrajul peretelui de subsol */
    spatiu_lucru_m: number;
    inel_jos: [number, number][][];
    inel_sus: [number, number][][];
    nota: string;
  } | null;
  santuri: {
    ax?: string;
    x1: number; y1: number; x2: number; y2: number;
    L_m: number;
    /** lățimea ȘANȚULUI la talpă — betonul plus spațiul de lucru */
    B_m: number;
    /** lățimea BETONULUI (talpa), pentru comparație */
    B_beton_m: number;
    b_inima_m?: number | null;
    motiv_latime?: string;
    Df_m: number;
    // panta malului șanțului ĂSTUIA: 0 sub înălțimea de mal vertical
    panta_m: number;
    B_sus_m: number; volum_m3: number;
    /** vederea complexului: al cărui corp e șanțul */
    corp?: string;
  }[];
  /** vederea complexului: adâncirile gropii generale sub radierele
   *  corpurilor, din fundul ei până sub radier și balast — aceleași inele
   *  pereche (talpă / gură) ca groapa */
  gropi_locale?: {
    corp: string;
    arie_m2: number; perimetru_m: number; H_m: number;
    z_sus_m: number; z_jos_m: number; h_radier_m: number;
    panta_m: number; regim?: FemRegimSapatura;
    volum_m3: number; volum_vertical_m3: number;
    inel_jos: [number, number][][]; inel_sus: [number, number][][];
    nota: string;
  }[];
  volum_locale_m3?: number;
  /** suma blocurilor corpurilor, săpate fiecare pe secțiunea lui: cât ar fi
   *  ieșit fără parcarea dintre ele */
  volum_corpuri_m3?: number | null;
  /** blocul e al întregului complex, nu al unui corp */
  complex?: boolean;
  corpuri?: string[];
  /** «santuri» (șanțuri pe axe) sau «generala» (toată amprenta săpată
   *  până la cota de fundare și umplută la loc; șanțurile rămân în listă
   *  cu geometria lor, dar fără volum) */
  regim_sapatura?: string;
  motiv_regim?: string;
  umplutura?: {
    umplutura_m3: number;
    beton_fundatie_m3: number;
    arie_amprenta_m2: number;
    gol_sub_pardoseala_m: number;
    articol_umplutura: string;
  } | null;
  volum_m3: number;
  volum_santuri_m3: number;
  volum_groapa_m3: number;
  volum_vertical_m3: number;
  volum_taluz_m3: number;
  h_pardoseala_sub_teren_m: number;
  adancime_totala_m: number;
  spatiu_lucru_m: number;
  articol_spatiu: string;
  /** perechile (lățime beton, lățime săpătură) distincte din proiect */
  latimi_sant_m: [number, number][];
  nota: string;
}

// Detaliul maselor seismice per nivel (tone) — docs/algoritm_eforturi.md P3
export interface MasaNivel {
  nivel_diafragma?: number;
  nivel?: string;
  placa_t?: number;
  grinzi_t?: number;
  stalpi_t?: number;
  pereti_grinzi_t?: number;
  pereti_placa_t?: number;
  finisaje_t?: number;
  utila_t?: number;
  zapada_t?: number; // pe placa de sub cer (acoperiș + terase)
  total_t?: number;
}

export interface FemSanity {
  arie_poligon_m2: number;
  arie_mesh_m2: number;
  acoperire_mesh_pct: number | null;
  // reperele EXTERIOARE: clădirea desenată (Pasul 2) și suprafața dintre
  // stâlpi. `acoperire_mesh_pct` compară mesh-ul cu poligonul de placă, deci
  // nu poate prinde o placă lipsă — ieșea ~100 % și când poligonul acoperea
  // 28 % din parter.
  arie_amprenta_m2?: number;
  acoperire_amprenta_pct?: number | null;
  arie_hull_stalpi_m2?: number;
  acoperire_hull_pct?: number | null;
  comparatie_lean?: {
    T1_lean_s?: number;
    T1_shell_s?: number;
    masa_lean_t?: number;
    // aria de placă a variantei lean: ea o ia DOAR din ochiurile închise de
    // grinzi, deci pe o rețea care nu se poligonizează rămâne mult sub shell
    arie_lean_m2?: number;
    delta_pct?: number;
    nota?: string;
    nota_arie?: string;
    eroare?: string;
  };
}

export interface FemResult {
  parametri: {
    varianta_model?: string;
    beton: string;
    E_gpa: number;
    finisaj: string;
    sdl_kn_m2: number;
    destinatie: string;
    q_kn_m2: number;
    psi_e: number;
    rigiditate_analiza_grinzi: number;
    rigiditate_analiza_stalpi: number;
    rigiditate_analiza_placa?: number;
    grosime_placa_m: number;
    b_eff_mod: number | null;
    inaltime_etaj_m: number;             // media reală a modelului
    inaltimi_etaj_m?: number[] | null;   // per etaj (Pasul 2 → Pasul 4)
    segmente_grinda: number | null;
    h_mesh_m?: number;
    h_mesh_efectiv_m?: number;
    nr_moduri?: number;
  };
  nr_noduri: number;
  nr_noduri_fem: number;
  nr_elemente: number;
  nr_shells?: number;
  nr_shells_pereti?: number;   // fâșiile panourilor de subsol
  nr_panouri?: number;   // ochiurile de placă (varianta shell)
  nr_stalpi: number;
  nr_grinzi: number;
  masa_totala_t: number;
  greutate_seismica_kn: number;
  diafragme: Omit<FemDiafragma, "sclavi">[];
  mase_detaliu?: MasaNivel[];
  sanity?: FemSanity;
  verificare_modala: FemVerificare;
  // raportul fundației din model (fem_fundatie.adauga_fundatie), plus impactul
  // pe dinamică. `activ: false` ⇒ baza e ÎNCASTRATĂ perfect, iar `motiv` spune
  // de ce — atunci fundația nu se desenează în scenă, fiindcă nu e în calcul.
  fundatie?: FemFundatie & {
    activ?: boolean;
    motiv?: string;
    nr_grinzi_fundare?: number;
    nr_resoarte?: number;
    nr_noduri_fundatie?: number;
    lungime_grinzi_m?: number;
    kv_total_kn_m?: number;
    // cum s-a așezat lanțul de grindă peste nodurile reale
    // (`fem_fundatie.lant_pe_ax`): reazemele excentrice legate transversal,
    // nodurile duse de alt ax și cât se depărtează lanțul de linia axului
    nr_transversale?: number;
    nr_noduri_sarite?: number;
    abatere_lant_max_m?: number;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    impact?: any;
    avertismente?: string[];
  };
  avertismente: string[];
  // parametrii pe care pasul i-a ales (componenta `DeciziiPas`) — de exemplu
  // materialul pereților de pe laturile balcoanelor
  decizii?: DecizieProiect[];
  model: FemModel;
  error?: string;
  // VEDEREA ÎNTREGULUI COMPLEX («Toate corpurile», `complex_corpuri.py`):
  // corpurile puse împreună, fiecare analizat separat. `corpuri` spune din ce
  // e făcută și de unde a venit fiecare (Pasul 7 sau schița lui).
  complex?: boolean;
  corpuri?: {
    cod: string; spatiu?: string | null; sursa?: "pas7" | "schita";
    origine?: number[]; dz_m?: number;
    nr_stalpi?: number; nr_grinzi?: number; nr_pereti_beton?: number;
    radier?: boolean;
    h_radier_m?: number | null;
  }[];
  /** peretele exterior al parcării: câți pereți, pe ce niveluri; `placa` e
   *  placa pe sol a parcării (conturul subsolului fără secțiunile corpurilor) */
  parcare?: {
    nr_pereti: number; niveluri: string[];
    placa?: { nr: number; arie_m2: number; grosime_m: number; balast_m?: number | null } | null;
  };
  /** corpurile care n-au încă niciun model (nici Pasul 4) */
  corpuri_fara_model?: string[];
}

